import { z } from 'zod';
import { FeedbackBoardSchema } from './platform.js';

export const MAX_FEEDBACK_SCREENSHOTS = 4;

const FeedbackScreenshotDataUrlSchema = z
  .string()
  .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/i, 'Invalid screenshot data URL');

export const FeedbackAgentContextSchema = z.object({
  agentId: z.string(),
  agentName: z.string().optional(),
  agentStatus: z.string().optional(),
  agentUrl: z.string().optional(),
});
export type FeedbackAgentContext = z.infer<typeof FeedbackAgentContextSchema>;

/**
 * Which deployment-wide board in-app posts go to. Client `boardId` is honored
 * only for configured Send feedback boards.
 */
export const FeedbackChannelSchema = z.enum(['feedback', 'bug']);
export type FeedbackChannel = z.infer<typeof FeedbackChannelSchema>;

/** One configured in-app destination. Names come from service-tier secrets, not a live board list. */
export const FeedbackDestinationSchema = FeedbackBoardSchema.extend({
  channel: FeedbackChannelSchema,
});
export type FeedbackDestination = z.infer<typeof FeedbackDestinationSchema>;

export const FeedbackDestinationsResponseSchema = z.object({
  destinations: z.array(FeedbackDestinationSchema),
});
export type FeedbackDestinationsResponse = z.infer<typeof FeedbackDestinationsResponseSchema>;

const feedbackScreenshotsField = z
  .array(FeedbackScreenshotDataUrlSchema)
  .max(MAX_FEEDBACK_SCREENSHOTS)
  .optional();

export const SubmitFeedbackSchema = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('create'),
    title: z.string().min(1),
    description: z.string().min(1),
    agentContext: FeedbackAgentContextSchema.optional(),
    screenshots: feedbackScreenshotsField,
    channel: FeedbackChannelSchema.optional(),
    boardId: z.string().optional(),
  }),
  z.object({
    mode: z.literal('attach'),
    postId: z.string().min(1),
    description: z.string().min(1),
    agentContext: FeedbackAgentContextSchema.optional(),
    screenshots: feedbackScreenshotsField,
    channel: FeedbackChannelSchema.optional(),
  }),
]);
export type SubmitFeedback = z.infer<typeof SubmitFeedbackSchema>;

export const SearchFeedbackPostsSchema = z.object({
  query: z.string().min(1),
  boardId: z.string().optional(),
  limit: z.number().int().min(1).max(25).optional(),
  channel: FeedbackChannelSchema.optional(),
});
export type SearchFeedbackPosts = z.infer<typeof SearchFeedbackPostsSchema>;

export const FeedbackSubmitResultSchema = z.object({
  postId: z.string(),
  url: z.string().nullable(),
});
export type FeedbackSubmitResult = z.infer<typeof FeedbackSubmitResultSchema>;

/** One related post offered by the popover before the user creates a duplicate. */
export const RelatedFeedbackPostSchema = z.object({
  id: z.string(),
  title: z.string(),
  status: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
});
export type RelatedFeedbackPost = z.infer<typeof RelatedFeedbackPostSchema>;

/**
 * A request the signed-in user has a stake in: one they filed, one they
 * upvoted from the popover, or both. Read live from the feedback backend so
 * the status is whatever the product team last set.
 */
export const MyFeedbackRequestSchema = z.object({
  id: z.string(),
  title: z.string(),
  details: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  url: z.string().nullable().optional(),
  boardName: z.string().nullable().optional(),
  score: z.number().nullable().optional(),
  commentCount: z.number().nullable().optional(),
  /** ISO timestamp from the backend, or null when it did not send one. */
  createdAt: z.string().nullable().optional(),
  /** True when the user opened the request. */
  authored: z.boolean(),
  /** True when the user upvoted the request (either directly or via the popover). */
  upvoted: z.boolean(),
});
export type MyFeedbackRequest = z.infer<typeof MyFeedbackRequestSchema>;

export const MyFeedbackResponseSchema = z.object({
  requests: z.array(MyFeedbackRequestSchema),
  /**
   * True when the deployment has no feedback backend configured. The page
   * renders an explanatory empty state instead of an error.
   */
  notConfigured: z.boolean(),
});
export type MyFeedbackResponse = z.infer<typeof MyFeedbackResponseSchema>;

export const FeedbackPlatformStatusSchema = z.object({
  hasApiKey: z.boolean(),
  boardId: z.string().nullable(),
  boardName: z.string().nullable(),
  /** Curated boards the Send feedback popover can target. Report bug is not in this list. */
  feedbackBoards: z.array(FeedbackBoardSchema),
  bugBoardId: z.string().nullable(),
  bugBoardName: z.string().nullable(),
});
export type FeedbackPlatformStatus = z.infer<typeof FeedbackPlatformStatusSchema>;
